import { getExpireToken } from '@/common/jwt.service'
import { AUTH_URL, CLIENT_ID, CLIENT_URI } from '@/common/config'
import { OAUTH, REFRESH_TOKEN } from '@/store/actions.type'
import moment from 'moment'

let authReady = false
let authRedirecting = false
let resolveAuthReady

const authReadyPromise = new Promise((resolve) => {
  resolveAuthReady = resolve
})

const AUTH_SKIP_WAIT_PATHS = [
  'auth/getAccessToken',
  'users',
]

const RETURN_URL_KEY = 'auth_return_url'

export function isAuthReady() {
  return authReady
}

export function isAuthRedirecting() {
  return authRedirecting
}

export async function waitForAuth() {
  if (authReady || authRedirecting) {
    return
  }
  await authReadyPromise
}

export function markAuthReady() {
  if (!authReady && !authRedirecting) {
    authReady = true
    resolveAuthReady?.()
  }
}

function markAuthRedirecting() {
  authRedirecting = true
}

export function shouldSkipAuthWait(resource) {
  if (!resource) {
    return false
  }
  const path = String(resource).replace(/^\//, '')
  return AUTH_SKIP_WAIT_PATHS.some((prefix) => path.startsWith(prefix))
}

async function generateCodeChallenge(codeVerifier) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier))

  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_')
}

function generateRandomString(length) {
  let text = ''
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length))
  }

  return text
}

function saveReturnUrl(route) {
  if (!route || route.query?.code) {
    return
  }

  const fullPath = route.fullPath || '/'
  if (fullPath === '/' || fullPath === '') {
    return
  }

  window.sessionStorage.setItem(RETURN_URL_KEY, fullPath)
}

export function consumeReturnUrl() {
  const url = window.sessionStorage.getItem(RETURN_URL_KEY)
  window.sessionStorage.removeItem(RETURN_URL_KEY)
  return url
}

function redirectToAuthProvider(route) {
  markAuthRedirecting()
  saveReturnUrl(route)

  const codeVerifier = generateRandomString(5)
  window.sessionStorage.setItem('code_verifier', codeVerifier)

  generateCodeChallenge(codeVerifier).then((codeChallenge) => {
    const args = new URLSearchParams({
      response_type: 'code',
      client_id: CLIENT_ID,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      redirect_uri: CLIENT_URI,
      state: Math.random(),
      scope: 'openid offline_access',
    })
    window.location = AUTH_URL + '?' + args
  })
}

async function ensureValidToken(store) {
  const expireToken = Number(getExpireToken())
  if (expireToken > 0 && moment().isAfter(moment.unix(expireToken))) {
    await store.dispatch(REFRESH_TOKEN)
  }
}

export async function initializeAuth(store, route) {
  if (typeof route.query.code !== 'undefined') {
    try {
      await store.dispatch(OAUTH, route.query.code)
      markAuthReady()
      return { status: 'oauth' }
    } catch {
      redirectToAuthProvider(route)
      return { status: 'redirect' }
    }
  }

  if (!store.getters.isAuthenticated && route.name !== 'Auth') {
    redirectToAuthProvider(route)
    return { status: 'redirect' }
  }

  if (store.getters.isAuthenticated && route.name === 'Auth') {
    try {
      await store.dispatch(OAUTH, route.query.access_token)
      markAuthReady()
      return { status: 'authenticated' }
    } catch {
      redirectToAuthProvider(route)
      return { status: 'redirect' }
    }
  }

  if (store.getters.isAuthenticated) {
    try {
      await ensureValidToken(store)
      markAuthReady()
      return { status: 'authenticated' }
    } catch {
      redirectToAuthProvider(route)
      return { status: 'redirect' }
    }
  }

  markAuthReady()
  return { status: 'anonymous' }
}

export async function navigateAfterAuth(router, userCardData) {
  const returnUrl = consumeReturnUrl()
  if (returnUrl) {
    await router.replace(returnUrl)
    return
  }

  if (userCardData?.isRegionalFundEmployee) {
    await router.replace({ name: 'CardRegions', params: { id: userCardData.relatedRegionId } })
  } else {
    await router.replace({ name: 'ListRegions' })
  }
}
